import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { LogIn, Lightbulb, Eye, EyeOff, Shield, ArrowLeft } from 'lucide-react';

const OtpInput = ({ value, onChange, disabled }) => {
  const refs = useRef([]);

  const handleChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const arr = value.split('');
    arr[idx] = val;
    const joined = arr.join('').slice(0, 6);
    onChange(joined);
    if (val && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) refs.current[idx - 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6);
    if (pasted) { onChange(pasted); refs.current[Math.min(pasted.length, 5)]?.focus(); }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-group">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <input
          key={i}
          ref={r => refs.current[i] = r}
          type="text"
          inputMode="numeric"
          maxLength="1"
          value={value[i] || ''}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          data-testid={`otp-digit-${i}`}
          className="w-11 h-14 text-center text-2xl font-black border-2 border-[var(--border-default)] bg-[var(--surface)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-all"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        />
      ))}
    </div>
  );
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const { login, loginWithMfa, cancelMfa, mfaRequired } = useAuth();
  const navigate = useNavigate();

  // Auto-submit when 6 digits entered
  const otpRef = useRef(otpCode);
  otpRef.current = otpCode;
  
  useEffect(() => {
    if (otpRef.current.length === 6 && mfaRequired && !loading) {
      handleMfaSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate('/');
    } else if (result.mfaRequired) {
      // MFA screen will show via context
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleMfaSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    const code = useBackupCode ? backupCode : otpCode;
    if (!code) { setError('Please enter a code'); setLoading(false); return; }
    const result = await loginWithMfa(code, useBackupCode);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
      if (!useBackupCode) setOtpCode('');
    }
    setLoading(false);
  };

  const handleBack = () => {
    cancelMfa();
    setOtpCode('');
    setBackupCode('');
    setUseBackupCode(false);
    setError('');
  };

  // MFA Verification Screen
  if (mfaRequired) {
    return (
      <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-[var(--obsidian)] border-2 border-[var(--border-default)] flex items-center justify-center">
                <Shield size={40} className="text-[#34C759]" />
              </div>
            </div>
            <h1 className="text-3xl font-black tracking-tighter mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Two-Factor Auth
            </h1>
            <p className="text-sm text-[var(--text-secondary)]">
              {useBackupCode ? 'Enter one of your backup codes' : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-8">
            {error && (
              <div className="bg-[#FF3B30]/10 border-2 border-[#FF3B30] p-4 mb-6">
                <p className="text-sm text-[#FF3B30] font-medium" data-testid="mfa-error">{error}</p>
              </div>
            )}

            <form onSubmit={handleMfaSubmit} className="space-y-6">
              {useBackupCode ? (
                <div>
                  <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                    Backup Code
                  </label>
                  <Input
                    value={backupCode}
                    onChange={e => setBackupCode(e.target.value.toUpperCase())}
                    placeholder="e.g. 1A2B3C4D"
                    className="w-full bg-[var(--surface)] border-2 border-[var(--border-default)] px-4 py-3 text-center text-lg font-mono tracking-widest"
                    style={{ borderRadius: 0 }}
                    data-testid="backup-code-input"
                  />
                </div>
              ) : (
                <OtpInput value={otpCode} onChange={setOtpCode} disabled={loading} />
              )}

              <button
                type="submit"
                disabled={loading || (useBackupCode ? !backupCode : otpCode.length < 6)}
                className="w-full selected-state font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="mfa-submit-btn"
              >
                <Shield size={20} />
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </form>

            <div className="mt-6 space-y-3 text-center">
              <button
                onClick={() => { setUseBackupCode(!useBackupCode); setError(''); }}
                className="text-sm text-[var(--text-secondary)] hover:text-[var(--obsidian)] underline transition-colors"
                data-testid="toggle-backup-code"
              >
                {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code'}
              </button>
              <div>
                <button
                  onClick={handleBack}
                  className="text-sm text-[var(--text-secondary)] hover:text-[var(--obsidian)] flex items-center gap-1 mx-auto transition-colors"
                  data-testid="mfa-back-btn"
                >
                  <ArrowLeft size={14} /> Back to login
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            <div className="w-20 h-20 bg-[var(--obsidian)] border-2 border-[var(--border-default)] flex items-center justify-center">
              <Lightbulb size={40} className="text-[#FFD60A]" />
            </div>
          </div>
          <h1 className="text-4xl font-black tracking-tighter mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            VAULT
          </h1>
          <p className="text-sm text-[var(--text-secondary)] font-mono uppercase tracking-wider">
            Idea Storage
          </p>
        </div>

        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-8">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Sign In
          </h2>

          {error && (
            <div className="bg-[#FF3B30]/10 border-2 border-[#FF3B30] p-4 mb-6">
              <p className="text-sm text-[#FF3B30] font-medium" data-testid="login-error">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-[var(--surface)] border-2 border-[var(--border-default)] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                style={{ borderRadius: 0 }}
                placeholder="your@email.com"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-[var(--surface)] border-2 border-[var(--border-default)] px-4 py-3 pr-12 text-base focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  style={{ borderRadius: 0 }}
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full selected-state font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              <LogIn size={20} />
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[var(--text-secondary)]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[var(--obsidian)] font-bold hover:underline">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
